import React, { ReactNode } from 'react';
import { v4 as uuidv4 } from 'uuid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import DeleteIcon from '@mui/icons-material/DeleteOutlineOutlined';
import EditIcon from '@mui/icons-material/EditOutlined';

import formatter from '../helpers/formatter';
import sort from '../helpers/sort';
import FormDialog from './FormDialog/FormDialog';
import type { Form } from '../types/form';
import { FormOperation, useFormDialog } from './FormDialog/useFormDialog';
import { Button } from '@mui/material';

type Props<T> = {
  items: T[];
  itemForm: Form<T>;
  headline: string;
  buttonLabel?: string;
  preallocateItem?: {
    label: string;
    value: Record<string, any>;
  };
  margin?: number;
  renderItemRow: (item: T, itemForm: Form<T>) => ReactNode;
  handleChange: (values: T[]) => void;
  handleSave?: (values: T[]) => void;
};

function ListItemField<T extends { id: string; date?: string }>({
  items,
  itemForm,
  headline,
  margin,
  buttonLabel,
  preallocateItem,
  renderItemRow,
  handleChange,
  handleSave,
}: Props<T>) {
  const onChange = React.useCallback(
    (updatedItem: T) => {
      const newItems = updateItems(updatedItem);
      handleChange(newItems);
    },
    [handleChange],
  );

  const onSave = React.useCallback(
    (updatedItem: T) => {
      const newItems = updateItems(updatedItem);
      if (handleSave !== null && handleSave !== undefined) {
        handleSave(newItems);
      }
    },
    [handleSave],
  );
  const { openDialog, dialogProps } = useFormDialog({ onSave, onChange, form: itemForm });
  const sortedItems = [...items].sort((itemA, itemB) => sort.sortByDate(itemA.date, itemB.date, 'DESC'));

  const updateItems = (updatedItem: T): T[] => {
    return items.find((item) => item.id === updatedItem.id)
      ? items.map((item) => {
          if (item.id === updatedItem.id) {
            return updatedItem;
          }
          return item;
        })
      : items.concat([updatedItem]);
  };

  const primaryButtonLabel = buttonLabel !== undefined ? buttonLabel : headline;

  return (
    <Stack direction='column' mt={margin} mb={margin}>
      <Stack direction='row' alignItems='center' width='100%'>
        <Typography fontSize='18px'>{headline}</Typography>
        <Stack direction='row' marginLeft='auto'>
          <Button
            aria-label={`${primaryButtonLabel} hinzufügen`}
            variant='outlined'
            startIcon={<AddCircleIcon />}
            onClick={(event) => {
              event.stopPropagation();
              const item = {
                id: uuidv4(),
                date: formatter.formatIsoDate(new Date().toString()),
              } as T;
              openDialog(item, FormOperation.CREATE);
            }}
          >
            {primaryButtonLabel}
          </Button>
          {preallocateItem != null && (
            <Button
              aria-label={`${preallocateItem.label} hinzufügen`}
              variant='outlined'
              sx={{ marginLeft: '1rem' }}
              onClick={(event) => {
                event.stopPropagation();
                const item = {
                  id: uuidv4(),
                  date: formatter.formatIsoDate(new Date().toString()),
                  ...preallocateItem.value,
                } as T;
                openDialog(item, FormOperation.CREATE);
              }}
              startIcon={<AddCircleIcon />}
            >
              {preallocateItem.label}
            </Button>
          )}
        </Stack>
      </Stack>
      {sortedItems.map((item) => (
        <Stack
          key={item.id}
          direction='row'
          spacing={3}
          mt={2}
          p='0px 5px 0px 15px'
          borderLeft='1px solid lightgrey'
          alignItems='center'
        >
          {renderItemRow(item, itemForm)}
          <IconButton
            aria-label='edit'
            onClick={() => {
              openDialog(item, FormOperation.UPDATE);
            }}
          >
            <EditIcon />
          </IconButton>
          <IconButton
            color='error'
            aria-label='delete'
            onClick={() => {
              const newItem = items.filter((i) => i.id !== item.id);
              handleChange(newItem);
              if (handleSave !== null && handleSave !== undefined) {
                handleSave(newItem);
              }
            }}
          >
            <DeleteIcon />
          </IconButton>
        </Stack>
      ))}
      {dialogProps && <FormDialog {...dialogProps} />}
    </Stack>
  );
}

export default ListItemField;
